import { useSelector } from 'react-redux';
import Groups from '@mui/icons-material/Groups';
import PageHeader from '../components/PageHeader';
import CheckCircleOutline from '@mui/icons-material/CheckCircleOutline';
import {
  Box, Container, Typography, Grid, Paper, ListItem, ListItemIcon,
  ListItemText, CircularProgress, Avatar
} from '@mui/material';
import { selectAboutData, selectAboutStatus } from '../redux/aboutSlice';

function About() {
  const aboutData = useSelector(selectAboutData);
  const status = useSelector(selectAboutStatus);

  if (status === 'loading' || status === 'idle') {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        backgroundImage="/images/headers/about_bg.jpeg"
        title={aboutData?.title || "DRIVEN BY PRECISION"}
        subtitle={aboutData?.subtitle || "Discover our legacy of community, excellence, and the pursuit of high-performance driving."}
      />

      {aboutData && (
        <>
          <Container maxWidth="lg" sx={{ py: 8 }}>
            {/* What is Gymkhana Section */}
            <Box sx={{ mb: 8 }}>
              <Typography
                variant="h2"
                sx={{
                  mb: 4,
                  fontFamily: 'Bebas Neue',
                  color: 'primary.main',
                  position: 'relative',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: -8,
                    left: 0,
                    width: 60,
                    height: 3,
                    bgcolor: 'secondary.main',
                  },
                }}
              >
                What is Auto-Gymkhana?
              </Typography>
              <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.8 }}>
                {aboutData?.whatis}
              </Typography>

              {/* Characteristics Section */}
              {aboutData?.characteristics && aboutData?.characteristics.length > 0 && (
                <Box sx={{ mt: 4 }}>
                  <Typography variant="h4" sx={{ mb: 3, color: 'primary.main', fontFamily: 'Bebas Neue' }}>
                    Key Characteristics
                  </Typography>
                  <Grid container spacing={2}>
                    {aboutData?.characteristics?.map((item, index) => (
                      <Grid item xs={12} sm={6} md={4} key={index}>
                        <Paper elevation={2} sx={{ p: 2, height: '100%', display: 'flex', alignItems: 'center' }}>
                          <ListItem>
                            <ListItemIcon>
                              <CheckCircleOutline color="primary" />
                            </ListItemIcon>
                            <ListItemText primary={item.value} />
                          </ListItem>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}
            </Box>

          </Container>

          <Box sx={{ bgcolor: 'grey.100', py: 8 }}>
            <Container maxWidth="lg">
              <Box>
                <Typography
                  variant="h2"
                  sx={{
                    mb: 4,
                    fontFamily: 'Bebas Neue',
                    color: 'primary.main',
                    position: 'relative',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      bottom: -8,
                      left: 0,
                      width: 60,
                      height: 3,
                      bgcolor: 'secondary.main',
                    },
                  }}
                >
                  Organization & Governance
                </Typography>
                <Paper
                  sx={{
                    p: 4,
                    background: 'linear-gradient(45deg, #001F3F 30%, #003366 90%)',
                    color: 'white',
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      right: 0,
                      width: '150px',
                      height: '150px',
                      background: 'radial-gradient(circle, rgba(255,215,0,0.1) 0%, rgba(255,215,0,0) 70%)',
                      borderRadius: '50%',
                    },
                  }}
                >
                  <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.8 }}>
                    {aboutData?.organization}
                  </Typography>

                  <Box sx={{ mt: 5 }}>
                    <Typography variant="h5" sx={{ color: '#fbbf24', mb: 3, fontFamily: 'Bebas Neue', letterSpacing: '2px', fontWeight: 'bold' }}>
                      AAGC LEADERSHIP COMMITTEE
                    </Typography>
                    <Grid container spacing={3}>
                      {[
                        { role: 'Director', name: aboutData?.director, image: aboutData?.director_image },
                        { role: 'Deputy Director', name: aboutData?.deputy_director, image: aboutData?.deputy_director_image },
                        { role: 'Development Executive', name: aboutData?.assistant, image: aboutData?.assistant_image },
                        { role: 'Coordinator', name: aboutData?.coordinator, image: aboutData?.coordinator_image }
                      ].map((leader, index) => (
                        <Grid item size={{ xs: 12, sm: 6 }} key={index}>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 3,
                              p: '16px 24px',
                              bgcolor: 'rgba(10, 25, 47, 0.7)',
                              backdropFilter: 'blur(12px)',
                              borderRadius: '16px',
                              border: '1px solid rgba(251, 191, 36, 0.15)',
                              position: 'relative',
                              overflow: 'hidden',
                              height: '110px',
                              transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                              '&::after': {
                                content: '""',
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                background: 'repeating-linear-gradient(45deg, rgba(255,255,255,0.02) 0px, rgba(255,255,255,0.02) 1px, transparent 1px, transparent 10px)',
                                pointerEvents: 'none',
                              },
                              '&:hover': {
                                borderColor: '#fbbf24',
                                transform: 'translateY(-4px) scale(1.02)',
                                boxShadow: '0 10px 30px rgba(0,0,0,0.5), inset 0 0 20px rgba(251, 191, 36, 0.1)',
                                '& .leader-avatar': {
                                  boxShadow: '0 0 25px rgba(251, 191, 36, 0.6)',
                                  transform: 'scale(1.05)',
                                }
                              }
                            }}
                          >
                            {/* Glowing Avatar */}
                            <Avatar
                              src={leader.image}
                              className="leader-avatar"
                              sx={{
                                width: 75,
                                height: 75,
                                border: '2px solid #fbbf24',
                                boxShadow: '0 0 15px rgba(251, 191, 36, 0.3)',
                                transition: 'all 0.4s ease',
                                zIndex: 1,
                                overflow: 'hidden',
                              }}
                            >
                              {!leader.image && <Groups sx={{ color: '#fbbf24' }} />}
                            </Avatar>

                            {/* Details */}
                            <Box sx={{ zIndex: 1, flexGrow: 1 }}>
                              <Box
                                sx={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  bgcolor: 'rgba(251, 191, 36, 1)',
                                  color: '#000',
                                  px: 1.2,
                                  pt: 0.5,
                                  pb: 0.3,
                                  borderRadius: '4px',
                                  mb: 0.8,
                                  fontSize: '0.7rem',
                                  fontWeight: 900,
                                  fontFamily: 'Bebas Neue',
                                  letterSpacing: '1.5px',
                                  textTransform: 'uppercase',
                                  lineHeight: 1,
                                  boxShadow: '0 4px 10px rgba(251, 191, 36, 0.3)',
                                }}
                              >
                                {leader.role}
                              </Box>
                              <Typography
                                variant="h5"
                                sx={{
                                  color: '#fff',
                                  fontFamily: 'Bebas Neue',
                                  fontSize: '1.6rem',
                                  fontWeight: 400,
                                  lineHeight: 1,
                                  textTransform: 'uppercase',
                                  letterSpacing: '1px',
                                  textShadow: '2px 2px 8px rgba(0,0,0,0.8), 0 0 20px rgba(251, 191, 36, 0.2)',
                                }}
                              >
                                {leader.name}
                              </Typography>
                            </Box>

                            {/* Decorative side accent */}
                            <Box
                              sx={{
                                position: 'absolute',
                                right: 0,
                                top: 0,
                                bottom: 0,
                                width: '4px',
                                bgcolor: '#fbbf24',
                                opacity: 0.4,
                              }}
                            />
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                </Paper>
              </Box>
            </Container>
          </Box>
        </>
      )}
    </Box>
  );
}

export default About;
